import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";

export function PrivacyPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const title = isSv ? "Integritetspolicy" : "Privacy policy";
  const desc = isSv
    ? "Information om hur CasinoMenu.se hanterar personuppgifter och cookies enligt GDPR."
    : "How CasinoMenu.se handles personal data and cookies under GDPR.";
  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: title }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <article className="prose prose-ink mt-8 max-w-3xl [&>h2]:mt-10 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3 [&>ul]:my-3 [&>ul]:list-disc [&>ul]:pl-6">
        <h2>{isSv ? "Sammanfattning" : "Summary"}</h2>
        <p>
          {isSv
            ? "CasinoMenu.se är en informationssajt utan användarkonton. Vi samlar in minimalt med personuppgifter och säljer aldrig data vidare. Den här policyn beskriver vad vi samlar in, varför och hur länge."
            : "CasinoMenu.se is an information site without user accounts. We collect minimal personal data and never sell it. This policy describes what we collect, why and for how long."}
        </p>

        <h2>{isSv ? "Vad vi samlar in" : "What we collect"}</h2>
        <ul>
          <li>
            <strong>{isSv ? "Loggdata: " : "Log data: "}</strong>
            {isSv
              ? "Cloudflare loggar IP-adress, useragent, geo-information och förfrågans väg när du laddar en sida. Detta används för att skydda mot DDoS-attacker och prestandaoptimering."
              : "Cloudflare logs IP address, user-agent, geo information and request path when you load a page. We use this for DDoS protection and performance."}
          </li>
          <li>
            <strong>{isSv ? "Klickstatistik: " : "Click statistics: "}</strong>
            {isSv
              ? "När du klickar på en /go/[brand]-länk loggar vi varumärke, sidkälla, placering, land, språk och tid. Vi loggar inte personliga ID:n."
              : "When you click a /go/[brand] link we log brand, source page, placement, country, language and timestamp. We do not log personal IDs."}
          </li>
          <li>
            <strong>{isSv ? "Cookies: " : "Cookies: "}</strong>
            {isSv
              ? "Vi använder en lokal cookie för cookie-samtycke och en för språkval. Inga cookies från tredjepartsannonsörer är aktiva."
              : "We use a local cookie for the consent banner and one for language preference. No third-party advertising cookies are active."}
          </li>
        </ul>

        <h2>{isSv ? "Vad vi inte samlar in" : "What we do not collect"}</h2>
        <p>
          {isSv
            ? "Vi har inga användarkonton, inga formulär som kräver personnummer eller kontonummer, och vi delar inte data med casinon. När du klickar på en /go-länk öppnas casinots egen sajt — där gäller deras egen integritetspolicy."
            : "We have no user accounts, no forms requiring SSN or bank account numbers, and we do not share data with casinos. When you click a /go link the casino's own site opens — its own privacy policy applies there."}
        </p>

        <h2>{isSv ? "Tredjepartstjänster" : "Third-party services"}</h2>
        <ul>
          <li>{isSv ? "Cloudflare Pages — hosting och DDoS-skydd." : "Cloudflare Pages — hosting and DDoS protection."}</li>
          <li>{isSv ? "Supabase — databas. Endast vår redaktionella data lagras här, ingen läsardata." : "Supabase — database. Only editorial data lives here, no reader data."}</li>
          <li>{isSv ? "Google Fonts — typsnitt laddas från fonts.googleapis.com." : "Google Fonts — fonts loaded from fonts.googleapis.com."}</li>
        </ul>

        <h2>{isSv ? "Dina rättigheter" : "Your rights"}</h2>
        <p>
          {isSv
            ? "Eftersom vi inte sparar några personuppgifter knutna till dig som individ finns det inte mycket att ”radera” — men du kan alltid skicka en GDPR-förfrågan till privacy@casinomenu.se så bekräftar vi vad som finns och raderar det inom 30 dagar."
            : "Because we do not store any personal data tied to you as an individual there is little to ”delete” — but you can always send a GDPR request to privacy@casinomenu.se and we will confirm what exists and delete it within 30 days."}
        </p>

        <h2>{isSv ? "Cookies" : "Cookies"}</h2>
        <p>
          {isSv
            ? "Den första gången du besöker sajten visas en cookie-banner. Du kan välja ”Endast nödvändiga” eller ”Acceptera alla”. Valet sparas i din lokala lagring och gäller framtida besök tills du rensar webbläsaren."
            : "The first time you visit the site a cookie banner appears. You can choose ”Necessary only” or ”Accept all”. The choice is saved in your local storage for future visits until you clear your browser."}
        </p>
      </article>

      <InternalLinks context="static" />
    </div>
  );
}
